import { ObjectId } from "mongodb";
import { z } from "zod";
import { FULL, typedCollection } from "../helpers/typedCollection.js";

// ---------------------------------------------------------------------------
// Shared sub-document schemas
// ---------------------------------------------------------------------------

/**
 * User reference embedded in createdBy / updatedBy fields.
 * A handful of legacy docs store an empty `{}` object here — fields are
 * therefore all optional.
 */
const userRef = z.object({
  _id: z.union([z.instanceof(ObjectId), z.string()]).optional(),
  fullName: z.string().optional(),
  profilePic: z.string().nullable().optional(),
});

/** Currency symbol embedded inside currency objects */
const currencySymbol = z.object({
  id: z.union([z.instanceof(ObjectId), z.string()]),
  name: z.string(),
});

/** Currency embedded in cost database */
const currencyObj = z.object({
  id: z.instanceof(ObjectId),
  name: z.string(),
  selectedSymbol: currencySymbol,
});

/** Cost database reference (embedded in EstimateVersions and latestEstimateVersion) */
const costDatabaseObj = z.object({
  id: z.instanceof(ObjectId).optional(),
  costDatabaseVersionId: z.instanceof(ObjectId).optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  defaultCostIndex: z.number().optional(),
  currency: currencyObj.optional(),
  costDatabaseVersion: z.number().optional(),
});

/** Display unit for a measurement type (area, linear, volume, count, angle) */
const displayUnitObj = z.object({
  id: z.instanceof(ObjectId),
  name: z.string(),
  factor: z.number(),
  selectedSymbol: z.object({
    id: z.string(),
    name: z.string(),
  }),
  type: z.object({
    id: z.instanceof(ObjectId),
    name: z.string(),
  }),
});

/** Decimal-place preferences per field — SSOT for stored shape (all fields required at write time) */
export const decimalPlacesObj = z.object({
  quantity: z.number(),
  measurement: z.number(),
  takeoff: z.number(),
  rate: z.number(),
  amount: z.number(),
});

/** Top-level settings block on EstimateVersions — SSOT for stored shape. Response schemas derive via `.partial()`. */
export const estimateSettingsObj = z.object({
  decimalPlaces: decimalPlacesObj,
  digitGrouping: z.string(),
  largeNumber: z.string(),
  /** Array of numberingSystem level values, one per BOQ depth */
  numberingSystem: z.array(z.number()),
});

export type EstimateDecimalPlaces = z.infer<typeof decimalPlacesObj>;
export type EstimateSettings = z.infer<typeof estimateSettingsObj>;

/**
 * Snapshot of takeoff-wizard config copied onto a file attachment.
 * Set when the file is added with the takeoff-wizard flow and re-run on
 * `upgradeFileVersionController`; `wizardRunPending` flips false once the run completes.
 */
const fileVersionTakeoffWizard = z.object({
  _id: z.instanceof(ObjectId),
  codeField: z.string(),
  quantityParameter: z.string(),
  unitField: z.string(),
  wizardRunPending: z.boolean(),
});

/** File version attachment inside EstimateVersions.fileVersions[] */
const fileVersionEntry = z.object({
  fileVersionId: z.instanceof(ObjectId),
  addedBy: userRef,
  addedDate: z.number(),
  takeoffWizard: fileVersionTakeoffWizard.optional(),
});

/** Reference variable used inside an additional-cost formula expression */
const additionalCostReference = z.object({
  id: z.string(),
  name: z.string(),
  serial: z.number(),
  value: z.number(),
  variable: z.string(),
});

/** Additional cost item inside EstimateVersions.additionalCost[] */
const additionalCostEntry = z.object({
  uniqueId: z.string(),
  name: z.string(),
  value: z.number(),
  createdBy: userRef,
  updatedBy: userRef,
  createdDate: z.number(),
  updatedDate: z.number(),
  /** Optional formula expression evaluated against `references` to derive `value`. */
  formula: z.string().nullable().optional(),
  /** Variables referenced from `formula`. Each entry maps `variable` → numeric `value`. */
  references: z.array(additionalCostReference).optional(),
});

/** Constituent estimate data embedded in merge-type EstimateVersions */
const constituentEstimateData = z.object({
  estimateId: z.instanceof(ObjectId),
  estimateVersionId: z.instanceof(ObjectId).optional(),
  amount: z.number(),
  name: z.string(),
  description: z.string().optional(),
  costDatabase: costDatabaseObj.optional(),
});

/** Constituent estimate version detail (merge type, stored in EstimateVersions) */
const constituentEstimateVersionDetail = z.object({
  name: z.string(),
  versionId: z.instanceof(ObjectId),
  estimateId: z.instanceof(ObjectId),
  amount: z.number(),
  description: z.string().optional(),
  versionNumber: z.number().nullable(),
  counter: z.number().optional(),
  itemId: z.instanceof(ObjectId).optional(),
});

/** Version note (added by createEstimateVersion / updateEstimateVersionNote) */
const versionNote = z.object({
  description: z.string(),
  updatedBy: z.instanceof(ObjectId),
  updatedOn: z.number(),
});

// ---------------------------------------------------------------------------
// Estimates collection
// ---------------------------------------------------------------------------

/** Embedded space reference inside Estimates */
const estimateSpaceRef = z.object({
  _id: z.instanceof(ObjectId),
  spaceName: z.string(),
  recordStatus: z.number(),
});

/** Embedded project reference inside Estimates */
const estimateProjectRef = z.object({
  _id: z.instanceof(ObjectId),
  projectId: z.string().optional(),
  projectName: z.string().optional(),
  description: z.string().optional(),
  recordStatus: z.number(),
});

/** User-permission entry inside Estimates.users[] */
const estimateUserEntry = z.object({
  id: z.instanceof(ObjectId),
  role: z.number(),
  permissions: z.array(z.number()),
});

/**
 * Denormalized snapshot of an EstimateVersions document embedded inside
 * Estimates.latestEstimateVersion and Estimates.latestPublishedEstimateVersion.
 *
 * This mirrors most fields from the EstimateVersions schema but lives inside
 * the parent Estimates document. Deeply nested display-unit and settings
 * sub-documents are typed via the shared schemas above.
 */
export const latestEstimateVersionEmbed = z.looseObject({
  name: z.string(),
  amount: z.number(),
  // Legacy docs store costIndex as a string.
  costIndex: z.union([z.number(), z.string()]).nullable(),
  estimateId: z.instanceof(ObjectId),
  description: z.string().nullable(),
  estimateVersionActivityType: z.string(),
  createdBy: userRef,
  updatedBy: userRef,
  versionNumber: z.number().nullable(),
  createdDate: z.number(),
  updatedDate: z.number(),
  dateVersion: z.number(),
  areaDisplayUnit: displayUnitObj.optional(),
  countDisplayUnit: displayUnitObj.optional(),
  linearDisplayUnit: displayUnitObj.optional(),
  volumeDisplayUnit: displayUnitObj.optional(),
  angleDisplayUnit: displayUnitObj.optional(),
  settings: estimateSettingsObj.optional(),
  manualMeasurementSheet: z.string().nullable().optional(),
  // Legacy docs store isLocked as a numeric 0/1.
  isLocked: z.union([z.boolean(), z.number()]).optional(),
  costDatabase: costDatabaseObj.nullable().optional(),
  estimateType: z.string().optional(),
  /** IDs of constituent estimates for merge-type estimates */
  constituentEstimateIds: z.array(z.string()).nullable().optional(),
  /** Detailed constituent estimate data for merge-type estimates */
  constituentEstimatesData: z.array(constituentEstimateData).optional(),
  /**
   * `_id` of the EstimateVersions document this snapshot was copied from. REQUIRED: every producing
   * path (`prepareEstimateVersionData` → `data.versionId = data._id`, `updateLatestUnNamedEstimateVersionDetails`,
   * `restoreEstimateVersion`, `createEstimate`) writes it, and staging agrees — present, non-null and
   * of BSON type objectId on 752/752 `Estimates` documents, and on all 252 that carry a
   * `latestPublishedEstimateVersion` (verified 2026-07-31).
   */
  versionId: z.instanceof(ObjectId),
  fileVersions: z.array(fileVersionEntry).optional(),
  /** Mirrors `EstimateVersions.additionalCost[]` for the current version. */
  additionalCost: z.array(additionalCostEntry).optional(),
  constituentEstimateVersionDetails: z.array(constituentEstimateVersionDetail).optional(),
  costDatabaseArray: z.array(costDatabaseObj).optional(),
  statusId: z
    .union([z.instanceof(ObjectId), z.string()])
    .nullable()
    .optional(),
  tags: z.array(z.any()).optional(),
});

export const estimateDocument = z.looseObject({
  _id: z.instanceof(ObjectId),
  /** Reference to the linked cost database (null for merge estimates or no Cost Database) */
  costDatabaseId: z.instanceof(ObjectId).nullable(),
  space: estimateSpaceRef,
  portalId: z.instanceof(ObjectId),
  project: estimateProjectRef,
  /** Auto-incrementing human-readable counter (e.g. displays as "ES-0034") */
  counter: z.number(),
  createdBy: userRef,
  createdDate: z.number(),
  status: z.string(),
  /** "standard" | "merge" — absent on legacy docs created before the type split. */
  estimateType: z.string().optional(),
  /** Denormalized snapshot of the current (draft) estimate version */
  latestEstimateVersion: latestEstimateVersionEmbed,
  /** Denormalized snapshot of the last named (published) estimate version */
  latestPublishedEstimateVersion: latestEstimateVersionEmbed.optional(),
  /** Array of constituent estimate ids for merge-type estimates (string or ObjectId). */
  constituentEstimateIds: z
    .array(z.union([z.instanceof(ObjectId), z.string()]))
    .nullable()
    .optional(),
  fileHierarchyId: z.instanceof(ObjectId).optional(),
  users: z.array(estimateUserEntry).optional(),
});

export type EstimateDocument = z.infer<typeof estimateDocument>;

// ---------------------------------------------------------------------------
// EstimateVersions collection
// ---------------------------------------------------------------------------

export const estimateVersionDocument = z.looseObject({
  _id: z.instanceof(ObjectId),
  name: z.string(),
  amount: z.number(),
  costIndex: z.union([z.number(), z.string()]).nullable(),
  estimateId: z.instanceof(ObjectId),
  description: z.string().nullable(),
  estimateVersionActivityType: z.string(),
  createdBy: userRef,
  updatedBy: userRef,
  /** null = draft/unnamed micro-version; integer = named/published version */
  versionNumber: z.number().nullable(),
  createdDate: z.number(),
  updatedDate: z.number(),
  dateVersion: z.number(),
  areaDisplayUnit: displayUnitObj.optional(),
  countDisplayUnit: displayUnitObj.optional(),
  linearDisplayUnit: displayUnitObj.optional(),
  volumeDisplayUnit: displayUnitObj.optional(),
  angleDisplayUnit: displayUnitObj.optional(),
  settings: estimateSettingsObj.optional(),
  manualMeasurementSheet: z.string().nullable().optional(),
  // Legacy docs write isLocked as a numeric flag (0/1).
  isLocked: z.union([z.boolean(), z.number()]),
  costDatabase: costDatabaseObj.nullable(),
  estimateType: z.string().optional(),
  /** Array of constituent estimate version details for merge estimates */
  constituentEstimateVersionDetails: z.array(constituentEstimateVersionDetail).optional(),
  /** All cost databases aggregated across constituent estimates */
  costDatabaseArray: z.array(costDatabaseObj).optional(),
  fileVersions: z.array(fileVersionEntry).optional(),
  additionalCost: z.array(additionalCostEntry).optional(),
  statusId: z
    .union([z.instanceof(ObjectId), z.string()])
    .nullable()
    .optional(),
  tags: z.array(z.any()).optional(),
  /** Optional text note attached to a named version */
  note: versionNote.optional(),
  /** Set to "deleted" when the owning estimate is deleted (cascade). */
  status: z.string().optional(),
});

export type EstimateVersionDocument = z.infer<typeof estimateVersionDocument>;

/**
 * The in-flight "current estimate version" object the version helpers hand around
 * (`generateOrFetchEstimateVersion` / `fetchLatestEstimateVersion` / the
 * `Estimates.latestEstimateVersion` embed): an EstimateVersions document whose `_id` has been
 * renamed to `versionId`, carrying whichever snapshot fields the producing path copied.
 *
 * Derived wholly from `estimateVersionDocument` — `estimateId` and `versionId` are the two fields
 * every producer guarantees, the rest stay optional because each copy path carries a different
 * subset. This is the single SoT for the `estimateVersion` parameter threaded through the BOQ
 * helpers (previously spelled out as an ad-hoc `Partial<…> & { versionId }` in two service files).
 */
export type EstimateVersionSnapshot = Partial<EstimateVersionDocument> &
  Pick<EstimateVersionDocument, "estimateId"> & { versionId: EstimateVersionDocument["_id"] };

// ---------------------------------------------------------------------------
// EstimateItems collection
// ---------------------------------------------------------------------------

/** parentsList entry — tracks the ancestor chain up to root ("0"). */
const parentsListEntry = z.object({
  // Legacy docs store itemId as an ObjectId rather than its string form.
  itemId: z.union([z.string(), z.instanceof(ObjectId)]),
  type: z.string().nullable(),
});

export type EstimateParentsListType = z.infer<typeof parentsListEntry>;

/** Measurement unit attached to a BOQ item */
const measurementUnitObj = z
  .object({
    // Legacy docs sometimes store id as a plain string.
    id: z.union([z.instanceof(ObjectId), z.string()]),
    name: z.string(),
    /** Conversion ratio string, e.g. "1:1" or "1:1000" */
    relation: z.string().optional(),
    selectedSymbol: z
      .object({
        id: z.string().optional(),
        name: z.string().optional(),
      })
      .optional(),
    factor: z.number().optional(),
  })
  .nullable();

/** Purchase unit attached to a BOQ item */
const purchaseUnitObj = z
  .object({
    id: z.instanceof(ObjectId),
    name: z.string(),
  })
  .nullable();

/**
 * Variable rate configuration for range-based pricing.
 * rules is stored as a JSON string — use z.any() to avoid double-parsing.
 */
const variableRateObj = z
  .object({
    rangeUnit: z.any(), // varies per rule
    isEnabled: z.boolean(),
    rules: z.string(), // JSON-encoded array of rule objects
  })
  .optional();

/**
 * Rate analysis block attached to a BOQ item.
 * rateAnalysisData.data entries are deeply nested and vary per item type.
 */
const rateAnalysisObj = z.object({
  purchaseUnitCount: z.number(),
  rateAnalysisData: z.object({
    data: z.array(z.any()), // complex nested rate analysis rows
    total: z.number(),
    say: z.number(),
  }),
});

export type EstimateItemRateAnalysis = z.infer<typeof rateAnalysisObj>;

export const estimateItemDocument = z.looseObject({
  _id: z.instanceof(ObjectId),
  estimateId: z.instanceof(ObjectId),
  estimateVersionId: z.instanceof(ObjectId),
  /** Stable business key for this item — preserved across version copy-on-write */
  itemId: z.instanceof(ObjectId),
  /** "ESTIMATE" | "COST_DATABASE" */
  type: z.string(),
  parentsList: z.array(parentsListEntry),
  /**
   * "0" = root level; otherwise the itemId of the parent.
   * Legacy docs store this as ObjectId or null instead of a string.
   */
  parentId: z.union([z.string(), z.instanceof(ObjectId)]).nullable(),
  level: z.number().nullable(),
  serial: z.number(),
  hasChild: z.boolean(),
  status: z.string(),
  description: z.string().nullable(),
  amount: z.number().nullable(),
  additionalAmount: z.number().optional(),
  /** Aggregated additional amount across constituent estimates for merge-type items. */
  additionalAmountForMerge: z.number().optional(),
  // Legacy docs occasionally store rate as NaN.
  rate: z.union([z.number(), z.nan()]).nullable(),
  costIndex: z.union([z.number(), z.string()]).nullable(),
  /** Quantity derived from takeoff measurements */
  quantityFromTakeoffs: z.number().nullable(),
  /** Manually entered quantity (overrides takeoff when set) */
  manualQuantity: z.number().nullable(),
  /** Legacy quantity field present on older documents */
  quantity: z.number().nullable().optional(),
  range: z.any().nullable(), // sparse/varied shape
  // Absent on a large share of legacy items.
  numberingSystem: z.number().optional(),
  createdBy: userRef.nullable().optional(),
  updatedBy: userRef.nullable().optional(),
  createdDate: z.number(),
  updatedDate: z.number(),
  // A number of legacy items have rateAnalysis stored as null.
  rateAnalysis: rateAnalysisObj.nullable(),
  isRateAnalysisEnable: z.boolean().optional(),
  measurementUnit: measurementUnitObj.optional(),
  purchaseUnit: purchaseUnitObj.optional(),
  variableRate: variableRateObj.nullable(),
  /** HSN / SAC tax code */
  HSN_SAC: z.string().nullable().optional(),
  /** Cost Database item code (e.g. "6.1C") */
  code: z.string().nullable().optional(),
  portalId: z.instanceof(ObjectId).optional(),
  /** Legacy field — item depth level from older documents */
  itemLevel: z.number().optional(),
  /** Legacy field — whether item has child records (older documents) */
  hasChildRecords: z.boolean().optional(),
  /**
   * Calculated quantity after applying measurementUnit.relation.
   * Legacy docs occasionally store this as an array of per-range quantities.
   */
  estimateQuantity: z.union([z.number(), z.array(z.any())]).optional(),
  /** Parent estimate id for merge-type constituent items */
  parentEstimateId: z.instanceof(ObjectId).optional(),
});

export type EstimateItemDocument = z.infer<typeof estimateItemDocument>;

/**
 * A merge-estimate "combined" BOQ row: an EstimateItems document whose `estimateQuantity` is pinned to
 * the ARRAY member of the schema's `number | unknown[]` union — one entry per constituent estimate that
 * contributed to the line (see `updateCombinedEstimateItems`). Derived entirely from
 * `estimateItemDocument`; the intersection selects a union member rather than restating any field.
 */
export type CombinedEstimateItemDocument = EstimateItemDocument & {
  estimateQuantity: Extract<EstimateItemDocument["estimateQuantity"], readonly unknown[]>;
};

// ---------------------------------------------------------------------------
// Typed collection accessors
// ---------------------------------------------------------------------------

export const Estimates = typedCollection("Estimates", estimateDocument);
export const EstimateVersions = typedCollection("EstimateVersions", estimateVersionDocument);
export const EstimateItems = typedCollection("EstimateItems", estimateItemDocument);

export const ESTIMATE_ITEMS_FULL_PROJECTION = {
  _id: 0,
  estimateId: 1,
  estimateVersionId: 1,
  itemId: 1,
  type: 1,
  parentsList: FULL,
  parentId: 1,
  level: 1,
  serial: 1,
  hasChild: 1,
  status: 1,
  description: 1,
  amount: 1,
  additionalAmount: 1,
  additionalAmountForMerge: 1,
  rate: 1,
  costIndex: 1,
  quantityFromTakeoffs: 1,
  manualQuantity: 1,
  quantity: 1,
  range: 1,
  numberingSystem: 1,
  createdBy: FULL,
  updatedBy: FULL,
  createdDate: 1,
  updatedDate: 1,
  rateAnalysis: FULL,
  isRateAnalysisEnable: 1,
  measurementUnit: FULL,
  purchaseUnit: FULL,
  variableRate: FULL,
  HSN_SAC: 1,
  code: 1,
  portalId: 1,
  itemLevel: 1,
  hasChildRecords: 1,
  estimateQuantity: 1,
  parentEstimateId: 1,
} as const;

/**
 * Full projection for EstimateVersions. Matches every field declared on
 * `estimateVersionDocument`. Excludes `_id` to mirror the redis-side
 * "fetch version, expose as versionId" pattern in `estimates.redis.ts`;
 * pass `_id: 1` inline in the call site if the caller actually needs it.
 */
export const ESTIMATE_VERSIONS_FULL_PROJECTION = {
  _id: 0,
  name: 1,
  amount: 1,
  costIndex: 1,
  estimateId: 1,
  description: 1,
  estimateVersionActivityType: 1,
  createdBy: FULL,
  updatedBy: FULL,
  versionNumber: 1,
  createdDate: 1,
  updatedDate: 1,
  dateVersion: 1,
  areaDisplayUnit: FULL,
  countDisplayUnit: FULL,
  linearDisplayUnit: FULL,
  volumeDisplayUnit: FULL,
  angleDisplayUnit: FULL,
  settings: FULL,
  manualMeasurementSheet: 1,
  isLocked: 1,
  costDatabase: FULL,
  estimateType: 1,
  constituentEstimateVersionDetails: FULL,
  costDatabaseArray: FULL,
  fileVersions: FULL,
  additionalCost: FULL,
  statusId: 1,
  tags: FULL,
  note: FULL,
} as const;

// --- Register in CollectionRegistry for typed db.collection() ---

declare module "../helpers/typedCollection.js" {
  interface CollectionRegistry {
    Estimates: EstimateDocument;
    EstimateVersions: EstimateVersionDocument;
    EstimateItems: EstimateItemDocument;
  }
}
